// ============================================================
// TILT — screens/RelayScreen.js        QUICK CO-OP: the lobby between balls
//
// caP'n, 2026-10-25: "One person at a time shoot a ball, do the best they can, next person comes in tries
// to finish the job, rinse repeat :3". The rules live in tiltRelay.js; this is only what you SEE between
// balls: who's in, whose ball it is, what's left in everyone's bank, and how the table stands.
// Kept small on purpose - the Empire never drowns the player in numbers.
// ============================================================
import { UI, PAGE_BG, PANEL, RAISED, facet, glow, ensureKeyframes } from "../theme";
import { btnStyle } from "../HUD";
import { START_BANK } from "../tiltRelay";

const PHASES = ["DORMANT", "STOKING", "AWAKE", "MELTDOWN"];
const PHASE_TINT = [UI.textDim, UI.brass, UI.hot, UI.bad];
const commas = (n) => Math.round(n || 0).toLocaleString();

function AnimaDots({ n, tint }) {
  const shown = Math.max(START_BANK, n);
  return (
    <span style={{ letterSpacing: 3, fontSize: 15 }}>
      {Array.from({ length: shown }, (_, i) => (
        <span key={i} style={{ color: i < n ? tint : UI.textFaint, textShadow: i < n ? glow(tint) : "none" }}>{i < n ? "●" : "○"}</span>
      ))}
    </span>
  );
}

export default function RelayScreen({ signedIn, seated, relay, onShoot, onBack }) {
  ensureKeyframes();
  const box = { background: PANEL, clipPath: facet(10), boxShadow: RAISED, padding: "14px 16px" };
  const t = relay && relay.table && relay.table.t;
  const phase = t ? t.phase | 0 : 0;

  let body;
  if (!signedIn) {
    body = <div style={{ ...box, color: UI.textDim, textAlign: "center" }}>Sign in with <b style={{ color: UI.brassBright }}>PLAY WITH FRIENDS</b> (top right) and bring a friend.</div>;
  } else if (!seated || !relay.ready) {
    body = <div style={{ ...box, color: UI.textDim, textAlign: "center" }}>Finding your server…</div>;
  } else if (!relay.run || relay.run.over || !relay.live) {
    const last = relay.run && relay.run.over ? relay.run : null;
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
        {last && (
          <div style={{ ...box, textAlign: "center", width: "100%", boxSizing: "border-box" }}>
            <div style={{ fontFamily: UI.fontD, fontSize: 12, letterSpacing: 3, color: UI.textDim }}>LAST RUN</div>
            <div style={{ fontFamily: UI.fontNum, fontSize: 26, color: UI.brassBright, textShadow: glow(UI.brassBright), marginTop: 4 }}>{commas(last.sc)}</div>
            {last.kills > 0 && <div style={{ color: UI.good, marginTop: 4 }}>Willie fell {last.kills > 1 ? `${last.kills} times` : "once"}</div>}
          </div>
        )}
        <div style={{ color: UI.textDim, textAlign: "center", fontSize: 14 }}>Everyone gets {START_BANK} Anima. One ball each, in turn.</div>
        <button onClick={relay.start} style={{ ...btnStyle(UI.cool), fontSize: 14, padding: "13px 30px" }}>START A RUN</button>
      </div>
    );
  } else {
    const { strip, myTurn, canJoin, imIn, turnName, run } = relay;
    const shooting = strip.find((p) => p.shooting);
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ ...box, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontFamily: UI.fontNum, fontSize: 24, color: UI.brassBright, textShadow: glow(UI.brassBright) }}>{commas(run.sc)}</span>
          <span style={{ fontFamily: UI.fontD, fontSize: 11, letterSpacing: 3, color: PHASE_TINT[phase] }}>WILLIE · {PHASES[phase]}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {strip.map((p) => (
            <div key={p.uid} style={{
              ...box, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10,
              boxShadow: p.turn ? `${RAISED}, inset 0 0 0 1px ${p.tint}` : RAISED, opacity: p.bank > 0 || p.turn ? 1 : 0.5,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: p.tint, boxShadow: glow(p.tint) }} />
              <span style={{ flex: 1, color: UI.text, fontWeight: p.me ? 700 : 500 }}>{p.n}{p.me ? " (you)" : ""}</span>
              {p.shooting && <span style={{ fontSize: 11, letterSpacing: 2, color: p.tint, animation: "tilt-flicker 2s linear infinite" }}>SHOOTING</span>}
              <AnimaDots n={p.bank} tint={p.tint} />
            </div>
          ))}
        </div>
        {myTurn && <button onClick={onShoot} style={{ ...btnStyle(UI.good), fontSize: 15, padding: "15px 0" }}>YOUR BALL — SHOOT</button>}
        {!myTurn && imIn && (
          <div style={{ textAlign: "center", color: UI.textDim, fontSize: 14 }}>
            {shooting ? `${shooting.n} is shooting…` : turnName ? `${turnName} is up next.` : "Passing the ball…"}
          </div>
        )}
        {canJoin && <button onClick={relay.join} style={{ ...btnStyle(UI.cool), padding: "12px 0" }}>JOIN THIS RUN</button>}
        {!imIn && !canJoin && <div style={{ textAlign: "center", color: UI.textFaint, fontSize: 13 }}>This run already started. You can join the next one.</div>}
        {imIn && <button onClick={relay.leave} style={{ ...btnStyle(UI.chromeDim), padding: "8px 0", fontSize: 11 }}>LEAVE THE RUN</button>}
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100dvh", background: PAGE_BG, fontFamily: UI.font, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", padding: "24px 16px", boxSizing: "border-box",
    }}>
      <div style={{ width: "min(380px, 100%)", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: UI.fontD, fontSize: 24, fontWeight: 900, letterSpacing: 6, color: UI.brassBright, textShadow: glow(UI.brassBright) }}>CO-OP</div>
          <div style={{ fontSize: 11, letterSpacing: 3, color: UI.textFaint, marginTop: 2 }}>CIRQUE DE J'YNX · FINISH THE JOB</div>
        </div>
        {body}
        <button onClick={onBack} style={{ ...btnStyle(UI.chromeDim), padding: "8px 0", fontSize: 11 }}>← BACK</button>
      </div>
    </div>
  );
}
